/**
 * Price <-> markup arithmetic for one RFQ line.
 *
 * The item row sets a price from five places (typing it, pasting supplier
 * text, inserting a saved remark, applying a suggestion, and the markup input
 * working backwards). Each used to repeat "set the price, and derive the
 * markup when there is a cost to derive it from". This is that rule, once.
 * Pure: the row decides what to save; this decides the numbers.
 */
import { calculateMarkup, roundTo99Cents } from "../../../api/client";

type CostPrice = number | null | undefined;

export interface AppliedPrice {
  /** Text for the price input: always two decimals. */
  price: string;
  /**
   * Text for the markup input (one decimal), or null when the line has no
   * positive cost price - the markup input is then left as it is.
   */
  markup: string | null;
  /** `markup` as the number saved to `markup_percentage`, or null. */
  markupPercentage: number | null;
}

/** Set a selling price; derive the markup when the cost price is above zero. */
export function applyPrice(costPrice: CostPrice, price: number): AppliedPrice {
  let markup: string | null = null;
  if (costPrice != null && costPrice > 0) {
    const derived = calculateMarkup(costPrice, price);
    if (!isNaN(derived)) markup = derived.toFixed(1);
  }
  return {
    price: Number(price).toFixed(2),
    markup,
    markupPercentage: markup == null ? null : parseFloat(markup),
  };
}

/**
 * Set a markup; returns the price input's text (cost x (1 + markup%),
 * rounded to the whole dollar), or null when there is no cost price or no
 * markup typed - the price input is then left as it is.
 */
export function applyMarkup(costPrice: CostPrice, markup: string): string | null {
  if (!costPrice || !markup) return null;
  return roundTo99Cents(costPrice * (1 + parseFloat(markup) / 100)).toFixed(2);
}
